import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

export default function Login() {
  const { login, formatApiError, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (params.get("error") === "oauth") toast.error("Google sign-in failed. Please try again.");
  }, [params]);

  useEffect(() => {
    if (user && user.role) {
      navigate(user.role === "super_admin" ? "/admin/super" : "/admin", { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await login(email, password);
      toast.success("Welcome back!");
      navigate(data.role === "super_admin" ? "/admin/super" : "/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex">
      <div className="hidden lg:flex flex-1 bg-[#2A2626] text-white p-12 flex-col justify-between relative overflow-hidden">
        <Link to="/" className="flex items-center gap-2 relative z-10" data-testid="login-back-home">
          <Logo theme="white" height={32} />
        </Link>
        <div className="relative z-10">
          <h2 className="text-4xl font-bold tracking-tight leading-tight">"Every Sunday, a fresh welcome."</h2>
          <p className="mt-6 text-white/70 max-w-md">Beautiful, distraction-free kiosks for first-time guests, connections cards, and event sign-ups.</p>
        </div>
        <div className="absolute -right-20 -bottom-20 w-96 h-96 rounded-full bg-[#34435A]/30 blur-3xl" />
      </div>
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <h1 className="text-4xl font-bold tracking-tight text-[#2A2626]">Welcome back</h1>
          <p className="mt-2 text-[#5C5856]">Sign in to manage your kiosk.</p>
          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-11" data-testid="login-email" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-sm text-[#5C5856] hover:text-[#2A2626] hover:underline" data-testid="login-forgot-password">Forgot password?</Link>
              </div>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 h-11" data-testid="login-password" />
            </div>
            <Button type="submit" disabled={busy} className="w-full h-11 bg-[#2A2626] hover:bg-black text-white rounded-full" data-testid="login-submit">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <div className="my-6 flex items-center gap-3 text-xs text-[#5C5856]">
            <div className="flex-1 h-px bg-[#E5E2DC]" /> or <div className="flex-1 h-px bg-[#E5E2DC]" />
          </div>
          <Button onClick={handleGoogle} variant="outline" className="w-full h-11 rounded-full border-[#E5E2DC]" data-testid="login-google">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </Button>
          <p className="mt-6 text-sm text-[#5C5856] text-center">
            New to MinistryHub? <Link to="/signup" className="text-[#2A2626] font-medium hover:underline" data-testid="login-signup-link">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
